import i18n from '@/util/config/i18n'
import { isStaticFirstRender } from '@/util/feature-flags'
import { isExportMode } from '@/util/canvas-render-mode'
import { EmbedType } from '@/modules/elements/models/Embed'
import { extractYoutubeVideoId } from '@/util/embed-link-parser'

/**
 * Click-to-load embeds (canvas load optimization, task 1.5): a third-party embed shows a card until the viewer clicks
 * it, so no player iframe or script loads on open. The card shows the video's thumbnail where the provider offers
 * one (YouTube by URL; Vimeo and Loom through their public oEmbed), otherwise the provider's name and logo. Shared by
 * embed elements, the rich-text iframe node and the static renderer, so every sink shows the same card and honours
 * the same loaded set.
 */

export type EmbedProviderId =
  | 'youtube'
  | 'vimeo'
  | 'loom'
  | 'drive'
  | 'calendly'
  | 'typeform'
  | 'canva'
  | 'pitch'
  | 'reclaim'
  | 'generic'

export type EmbedProvider = {
  id: EmbedProviderId
  /** Brand name, or the host for an unknown provider */
  label: string
  /** Host without `www.`; empty when the URL cannot be parsed */
  host: string
  /** A video player: the card plays it and the click starts playback */
  video: boolean
  /** NIcon name for the logo card */
  icon: string
}

/**
 * Why an embed element cannot load, as its card says: embed.js was blocked (`script`), the URL is not https
 * (`insecure`), or the stored embed has nothing that can load (`unembeddable`)
 */
export type EmbedBlockedReason = 'script' | 'insecure' | 'unembeddable'

// What the URL parser strips before parsing: C0 controls and spaces at either end, tabs and newlines anywhere (so
// `java\tscript:` is `javascript:`). Nothing else: a space inside the scheme makes `ht tps:` a relative URL.
// eslint-disable-next-line no-control-regex
const URL_TRIMMED_ENDS = /^[\u0000- ]+|[\u0000- ]+$/g
const URL_REMOVED_CHARS = /[\t\n\r]/g

const PROVIDERS: Record<EmbedProviderId, { label: string; icon: string; video?: boolean }> = {
  youtube: { label: 'YouTube', icon: 'youtube', video: true },
  vimeo: { label: 'Vimeo', icon: 'video-camera', video: true },
  loom: { label: 'Loom', icon: 'loom', video: true },
  drive: { label: 'Google Drive', icon: 'google-drive' },
  calendly: { label: 'Calendly', icon: 'calendly' },
  typeform: { label: 'Typeform', icon: 'typeform' },
  canva: { label: 'Canva', icon: 'canva' },
  pitch: { label: 'Pitch', icon: 'pitch' },
  reclaim: { label: 'Reclaim', icon: 'puzzle' },
  generic: { label: '', icon: 'puzzle' }
}

const TYPE_PROVIDERS: Partial<Record<EmbedType, EmbedProviderId>> = {
  [EmbedType.YOUTUBE]: 'youtube',
  [EmbedType.LOOM]: 'loom',
  [EmbedType.GDOCS]: 'drive',
  [EmbedType.CALENDLY]: 'calendly',
  [EmbedType.TYPEFORM]: 'typeform',
  [EmbedType.CANVA]: 'canva',
  [EmbedType.PITCH]: 'pitch',
  [EmbedType.RECLAIM]: 'reclaim'
}

// Host suffix → provider; Vimeo has no EmbedType of its own (it is stored as VIDEO)
const HOST_PROVIDERS: [string, EmbedProviderId][] = [
  ['youtube.com', 'youtube'],
  ['youtube-nocookie.com', 'youtube'],
  ['youtu.be', 'youtube'],
  ['vimeo.com', 'vimeo'],
  ['loom.com', 'loom'],
  ['drive.google.com', 'drive'],
  ['docs.google.com', 'drive'],
  ['calendly.com', 'calendly'],
  ['typeform.com', 'typeform'],
  ['canva.com', 'canva'],
  ['pitch.com', 'pitch'],
  ['reclaim.ai', 'reclaim']
]

// oEmbed endpoints that answer cross-origin requests with a thumbnail_url (checked 2026-09-30)
const OEMBED_ENDPOINTS: Partial<Record<EmbedProviderId, (url: string) => string>> = {
  vimeo: url => `https://vimeo.com/api/oembed.json?width=960&url=${encodeURIComponent(url)}`,
  loom: url => `https://www.loom.com/v1/oembed?url=${encodeURIComponent(url.replace('/embed/', '/share/'))}`
}
const OEMBED_TIMEOUT_MS = 5000
const MAX_THUMBNAILS = 200

const loadedEmbeds = new Set<string>()
// One lookup per URL per session; an answer without a thumbnail is remembered as null, so it is not asked again on
// every render. A lookup that got no answer (timeout, network error, error status) is forgotten and may be retried.
const thumbnails = new Map<string, Promise<string | null>>()

/** Cards instead of live embeds: behind `static_first_render`, and never in export (see embed-print-card.ts). */
export function isClickToLoadEnabled(): boolean {
  return isStaticFirstRender() && !isExportMode()
}

function hostOf(url: string): string {
  try {
    return new URL(url, 'https://invalid.local').hostname.replace(/^www\./, '').replace(/^invalid\.local$/, '')
  } catch {
    return ''
  }
}

function matchesHost(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`)
}

export function embedProvider(url: string | null | undefined, embedType?: EmbedType): EmbedProvider {
  const host = url ? hostOf(url) : ''
  const byHost = HOST_PROVIDERS.find(([suffix]) => matchesHost(host, suffix))?.[1]
  const id = (embedType && TYPE_PROVIDERS[embedType]) || byHost || 'generic'
  const known = PROVIDERS[id]
  return { id, label: known.label || host, host, video: !!known.video, icon: known.icon }
}

/**
 * The URL an embed may load, as the browser parses it: https only (`//host` counts as https); anything else is
 * refused. Bind the returned URL rather than the string checked, so what loads is what was checked.
 */
export function toSafeEmbedSrc(url: string | null | undefined): string | null {
  if (!url) return null
  const trimmed = url.replace(URL_TRIMMED_ENDS, '').replace(URL_REMOVED_CHARS, '')
  const absolute = trimmed.startsWith('//') ? `https:${trimmed}` : trimmed
  try {
    const parsed = new URL(absolute)
    return parsed.protocol === 'https:' ? parsed.href : null
  } catch {
    return null
  }
}

/** The src for a video loaded by a click: it starts playing (all three accept `autoplay=1`). The stored URL is untouched. */
export function withAutoplay(src: string, provider: EmbedProvider): string {
  if (!provider.video) return src
  try {
    const url = new URL(src)
    url.searchParams.set('autoplay', '1')
    return url.href
  } catch {
    return src
  }
}

/** A thumbnail known from the URL alone (YouTube), so it can be part of cached static HTML. */
export function immediateThumbnail(url: string | null | undefined, provider: EmbedProvider): string | null {
  if (provider.id !== 'youtube' || !url) return null
  const id = extractYoutubeVideoId(url)
  return id ? `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg` : null
}

/**
 * Off unless `VITE_EMBED_THUMBNAIL_LOOKUP=true`: the lookup reaches Vimeo or Loom when a card renders, before the viewer
 * has clicked, so their IP and the embed URL would go to a third party without consent. Cards use the logo instead.
 */
export function isThumbnailLookupEnabled(): boolean {
  return import.meta.env.VITE_EMBED_THUMBNAIL_LOOKUP === 'true'
}

/** Whether the provider's thumbnail needs an oEmbed lookup (Vimeo, Loom) and lookups are enabled. */
export function hasLookupThumbnail(provider: EmbedProvider): boolean {
  return isThumbnailLookupEnabled() && !!OEMBED_ENDPOINTS[provider.id]
}

/**
 * The provider's thumbnail through its public oEmbed endpoint, or null. Sends no cookies and no referrer, so the
 * provider never learns which space asked; the thumbnail must be https.
 */
export function lookupThumbnail(url: string, provider: EmbedProvider): Promise<string | null> {
  const endpoint = OEMBED_ENDPOINTS[provider.id]
  const safeUrl = toSafeEmbedSrc(url)
  if (!endpoint || !safeUrl) return Promise.resolve(null)
  let pending = thumbnails.get(safeUrl)
  if (!pending) {
    if (thumbnails.size >= MAX_THUMBNAILS) thumbnails.clear()
    const lookup: Promise<string | null> = fetchOEmbedThumbnail(endpoint(safeUrl)).catch(() => {
      // Unless the cache was cleared meanwhile
      if (thumbnails.get(safeUrl) === lookup) thumbnails.delete(safeUrl)
      return null
    })
    pending = lookup
    thumbnails.set(safeUrl, pending)
  }
  return pending
}

/** Rejects when the provider gave no answer: timeout, network error, error status or an unreadable body. */
async function fetchOEmbedThumbnail(endpoint: string): Promise<string | null> {
  const controller = typeof AbortController === 'undefined' ? null : new AbortController()
  const timer = controller ? setTimeout(() => controller.abort(), OEMBED_TIMEOUT_MS) : null
  try {
    const response = await fetch(endpoint, {
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      signal: controller?.signal
    })
    if (!response.ok) throw new Error(`oEmbed lookup failed with ${response.status}`)
    const body = (await response.json()) as { thumbnail_url?: unknown }
    return typeof body.thumbnail_url === 'string' ? toSafeEmbedSrc(body.thumbnail_url) : null
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export const elementEmbedKey = (elementId: string): string => `element:${elementId}`
export const srcEmbedKey = (src: string): string => `src:${src}`

export function markEmbedLoaded(key: string): void {
  loadedEmbeds.add(key)
}

export function isEmbedLoaded(key: string): boolean {
  return loadedEmbeds.has(key)
}

/** On space leave and logout, like the static render cache: what one viewer loaded or looked up never carries over. */
export function clearLoadedEmbeds(): void {
  loadedEmbeds.clear()
  thumbnails.clear()
}

export const EMBED_CARD_CLASS = 'embed-card'

/**
 * The card's visible title: "Play YouTube video" for a video, "Load Calendly" otherwise. Without a provider name the
 * whole sentence has its own key, as "{provider}" cannot take a noun with the right article and case in every locale.
 */
export function embedCardTitle(provider: EmbedProvider): string {
  if (!provider.label) return i18n.t('embeds.clickToLoad.loadContent') as string
  const key = provider.video ? 'embeds.clickToLoad.play' : 'embeds.clickToLoad.load'
  return i18n.t(key, { provider: provider.label }) as string
}

export function embedCardHint(provider: EmbedProvider): string {
  return provider.host ? (i18n.t('embeds.clickToLoad.from', { host: provider.host }) as string) : ''
}

/**
 * Shows a thumbnail on a card (DOM path). An image that fails to load is removed, leaving the logo card. The image is
 * lazy and sends no referrer, like the oEmbed lookup.
 */
export function setCardThumbnail(card: HTMLElement, thumbnail: string): void {
  if (card.querySelector(`.${EMBED_CARD_CLASS}__thumbnail`)) return
  const img = card.ownerDocument.createElement('img')
  img.className = `${EMBED_CARD_CLASS}__thumbnail`
  img.setAttribute('alt', '')
  img.setAttribute('loading', 'lazy')
  img.setAttribute('decoding', 'async')
  img.setAttribute('referrerpolicy', 'no-referrer')
  // Attributes, not properties: cards from the static renderer are serialized to HTML
  img.setAttribute('src', thumbnail)
  card.classList.add(`${EMBED_CARD_CLASS}--thumbnail`)
  card.prepend(img)
  watchCardThumbnail(card)
}

/**
 * Falls back to the logo card if the card's thumbnail fails to load. Cards from cached static HTML lose their
 * listeners in serialization, so TextEditor calls this again once they are in the page.
 */
export function watchCardThumbnail(card: HTMLElement): void {
  const img = card.querySelector<HTMLImageElement>(`.${EMBED_CARD_CLASS}__thumbnail`)
  if (!img) return
  const fallBack = () => {
    img.remove()
    card.classList.remove(`${EMBED_CARD_CLASS}--thumbnail`)
  }
  // Already failed before a listener could be attached
  if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) {
    fallBack()
    return
  }
  img.addEventListener('error', fallBack, { once: true })
}

/** Adds a looked-up thumbnail (Vimeo, Loom) to a card built from cached HTML or a node view. */
export function hydrateEmbedCard(card: HTMLElement): void {
  const src = card.getAttribute('data-embed-src')
  if (!src) return
  const provider = embedProvider(src)
  if (!hasLookupThumbnail(provider)) return
  lookupThumbnail(src, provider).then(thumbnail => {
    // A card already swapped for its iframe has no parent; a node view's card may not be in the page yet
    if (thumbnail && card.parentNode) setCardThumbnail(card, thumbnail)
  })
}

/**
 * The card for a rich-text iframe (node view and static renderer). Built with DOM APIs only, so provider or host text
 * can never become markup. A src that is not https gets a disabled card and can never load.
 */
export function createEmbedCard(doc: Document, src: string | null | undefined): HTMLButtonElement {
  const safeSrc = toSafeEmbedSrc(src)
  const provider = embedProvider(src)
  const button = doc.createElement('button')
  button.type = 'button'
  button.className = EMBED_CARD_CLASS
  const play = doc.createElement('span')
  play.className = `${EMBED_CARD_CLASS}__play`
  play.setAttribute('aria-hidden', 'true')
  const text = doc.createElement('span')
  text.className = `${EMBED_CARD_CLASS}__text`
  const title = doc.createElement('span')
  title.className = `${EMBED_CARD_CLASS}__title`
  // No aria-label: the visible title and hint name the button. The loading state is announced by a status region
  // next to the card (see loadEmbedBehindCard), since a button's content is not read out as a live region.
  const hint = doc.createElement('span')
  hint.className = `${EMBED_CARD_CLASS}__hint`
  if (safeSrc) {
    button.setAttribute('data-embed-src', safeSrc)
    title.textContent = embedCardTitle(provider)
    hint.textContent = embedCardHint(provider)
  } else {
    button.disabled = true
    title.textContent = i18n.t('embeds.clickToLoad.blocked') as string
  }
  text.append(title, hint)
  button.append(play, text)
  const thumbnail = safeSrc && immediateThumbnail(safeSrc, provider)
  if (thumbnail) setCardThumbnail(button, thumbnail)
  return button
}

/** The live iframe that replaces a card, with the attributes the rich-text iframe node renders. */
export function createEmbedIframe(doc: Document, src: string, autoplay: boolean): HTMLIFrameElement {
  const iframe = doc.createElement('iframe')
  iframe.className = EMBED_IFRAME_CLASS
  iframe.setAttribute('src', autoplay ? withAutoplay(src, embedProvider(src)) : src)
  iframe.setAttribute('frameborder', '0')
  iframe.setAttribute('allowfullscreen', 'true')
  iframe.setAttribute('allow', 'autoplay; fullscreen')
  return iframe
}

/** A rich-text iframe loaded through a card; sized like the card, so loading it does not move the text below */
export const EMBED_IFRAME_CLASS = 'embed-iframe'
/** On the `iframe-wrapper` of a rich-text iframe that shows a card, so the card and its player share one box */
export const EMBED_WRAPPER_CLASS = 'iframe-wrapper--click-to-load'
/** How long a loading card waits for the player's load event before stepping aside anyway */
export const PLAYER_REVEAL_TIMEOUT_MS = 10000
// Matches the card's fade-out in _embed-card.scss
const CARD_FADE_MS = 200

export function embedCardLoadingTitle(provider: EmbedProvider): string {
  if (!provider.label) return i18n.t('embeds.clickToLoad.loadingContent') as string
  return i18n.t('embeds.clickToLoad.loading', { provider: provider.label }) as string
}

export function isCardLoading(card: Element): boolean {
  return card.classList.contains(`${EMBED_CARD_CLASS}--loading`)
}

/**
 * Calls `done` once the player in or under `target` has loaded (or failed to), or after `timeoutMs`, whichever comes
 * first; a player that never reports must not leave the viewer behind a spinner. Returns a cancel function.
 */
export function onPlayerLoaded(
  target: EventTarget,
  done: () => void,
  timeoutMs = PLAYER_REVEAL_TIMEOUT_MS
): () => void {
  let finished = false
  // `load` does not bubble; a capture listener also hears iframes an embed script adds inside `target`
  const cancel = () => {
    clearTimeout(timer)
    target.removeEventListener('load', finish, true)
    target.removeEventListener('error', finish, true)
  }
  const finish = () => {
    if (finished) return
    finished = true
    cancel()
    done()
  }
  const timer = setTimeout(finish, timeoutMs)
  target.addEventListener('load', finish, true)
  target.addEventListener('error', finish, true)
  return cancel
}

/**
 * Loads a card's player behind the card (DOM path: rich-text node view and static content). The card stays on top in
 * its loading state, thumbnail and all, and fades out once the player has loaded, so the viewer never looks at an
 * empty frame. Focus follows to the player when the card had it. Returns a function that stops the wait, for owners
 * whose DOM goes away first (a destroyed node view or component).
 */
export function loadEmbedBehindCard(card: HTMLElement, src: string, autoplay: boolean): () => void {
  const doc = card.ownerDocument
  const iframe = createEmbedIframe(doc, src, autoplay)
  const hadFocus = doc.activeElement === card
  const loadingTitle = embedCardLoadingTitle(embedProvider(src))
  card.classList.add(`${EMBED_CARD_CLASS}--loading`)
  card.setAttribute('aria-busy', 'true')
  card.setAttribute('aria-disabled', 'true')
  const title = card.querySelector(`.${EMBED_CARD_CLASS}__title`)
  if (title) title.textContent = loadingTitle
  // "Loads when you click" no longer applies once clicked
  card.querySelector(`.${EMBED_CARD_CLASS}__hint`)?.remove()
  card.before(iframe)
  const status = cardStatus(card)
  // Filled once the region is in the page: text that arrives with the region itself is often not announced
  const statusTimer = setTimeout(() => (status.textContent = loadingTitle), STATUS_DELAY_MS)
  let fadeTimer: ReturnType<typeof setTimeout> | null = null
  const cancelWait = onPlayerLoaded(iframe, () => {
    clearTimeout(statusTimer)
    card.classList.add(`${EMBED_CARD_CLASS}--leaving`)
    fadeTimer = setTimeout(() => {
      card.remove()
      status.remove()
    }, CARD_FADE_MS)
    if (hadFocus && doc.activeElement === card) iframe.focus()
  })
  return () => {
    cancelWait()
    clearTimeout(statusTimer)
    if (fadeTimer) clearTimeout(fadeTimer)
  }
}

/** Visually hidden, outside the card's button: a button's content is presentational and not read as a live region */
export const EMBED_CARD_STATUS_CLASS = 'embed-card-status'
const STATUS_DELAY_MS = 100

/** The card's status region, added after it on first use. */
function cardStatus(card: HTMLElement): HTMLElement {
  const next = card.nextElementSibling as HTMLElement | null
  if (next?.classList.contains(EMBED_CARD_STATUS_CLASS)) return next
  const status = card.ownerDocument.createElement('span')
  status.className = EMBED_CARD_STATUS_CLASS
  status.setAttribute('role', 'status')
  card.after(status)
  return status
}
